import type { Config } from '@wagmi/vue'

/**
 * Installs wagmi, the wallet stack, and provides `$loadWagmi` to install it on demand.
 *
 * The landing page does not wait for it: there the stack loads only when something needs it,
 * the wallet button or a session signed with a wallet, both after hydration. Every other page
 * uses wagmi as it renders, so it is installed before the app starts there and before any
 * navigation away from the landing page.
 */
export default defineNuxtPlugin(async (nuxtApp) => {
  let installing: Promise<Config> | null = null

  async function loadWagmi(): Promise<Config> {
    installing ??= (async () => {
      const [{ WagmiPlugin }, { createMinimalWagmiConfig, getFullWagmiConfig }] = await Promise.all([
        import('@wagmi/vue'),
        import('~/config/wagmi'),
      ])
      // The config reads the runtime config, which needs the Nuxt context this await left.
      const config = await nuxtApp.runWithContext(() =>
        import.meta.server ? createMinimalWagmiConfig() : getFullWagmiConfig(),
      )
      nuxtApp.vueApp.use(WagmiPlugin, { config, reconnectOnMount: import.meta.client })
      return config
    })()
    return installing
  }

  if (import.meta.client) {
    addRouteMiddleware('wagmi', async (to) => {
      if (to.path !== '/')
        await loadWagmi()
    }, { global: true })
  }

  if (import.meta.server || window.location.pathname !== '/')
    await loadWagmi()

  return {
    provide: {
      loadWagmi,
    },
  }
})
